export const oneSecondInMs = 1000

export const oneMinInSecs = 60
export const oneHourInSecs = 60 * oneMinInSecs
export const oneDayInSecs = 24 * oneHourInSecs
export const oneWeekInSecs = 7 * oneDayInSecs
export const oneYearInSecs = 365 * oneDayInSecs

const oneMinInMs = oneMinInSecs * oneSecondInMs
const oneDayInMs = oneDayInSecs * oneSecondInMs

export function hours(hrs: number) {
  return {
    toSecs: () => hrs * oneHourInSecs
  }
}

export function days(_days: number) {
  return {
    toSecs: () => _days * oneDayInSecs,
    toMs: () => _days * oneDayInMs
  }
}

export function mins(_mins: number) {
  return {
    toSecs: () => _mins * oneMinInSecs,
    toMs: () => _mins * oneMinInMs
  }
}

export function secs(_secs: number) {
  return {
    toMs: () => _secs * oneSecondInMs
  }
}

export function ms(_ms: number) {
  return {
    toSecs: () => _ms / oneSecondInMs
  }
}

export function toJsTimestamp(unixTimestamp: number): number {
  return unixTimestamp * oneSecondInMs
}

export function nowInSeconds(): number {
  return Math.floor(Date.now() / oneSecondInMs)
}

interface FormatDurationOptions {
  /** Unit format. `'short'` → `"5h"`, `'long'` → `"5 hours"`. Defaults to `'short'`. */
  format?: 'short' | 'long'
  /** When true, also show the next-smaller unit if non-zero (e.g. `"5d 3h"` vs `"5d"`). Defaults to false. */
  granular?: boolean
}

function formatUnit(n: number, unit: 'day' | 'hour' | 'minute', format: 'short' | 'long'): string {
  if (format === 'short') {
    const abbr = unit === 'day' ? 'd' : unit === 'hour' ? 'h' : 'm'
    return `${n}${abbr}`
  }
  return `${n} ${n === 1 ? unit : `${unit}s`}`
}

/**
 * Formats a duration in seconds as a human-readable string.
 *
 * By default, renders the largest unit only in short form (e.g. `"3d"`, `"5h"`).
 * Pass `{ format: 'long' }` for full words (e.g. `"3 days"`). Pass
 * `{ granular: true }` to also include the next-smaller unit when non-zero
 * (e.g. `"3d 12h"`, `"5 hours, 30 minutes"`).
 *
 * Returns `null` for non-positive input. Floors positive sub-minute values to `"1m"`.
 */
export function formatDuration(
  seconds: number,
  { format = 'short', granular = false }: FormatDurationOptions = {}
): string | null {
  if (seconds <= 0) return null

  const sep = format === 'short' ? ' ' : ', '

  if (seconds >= oneDayInSecs) {
    const d = Math.floor(seconds / oneDayInSecs)
    if (!granular) return formatUnit(d, 'day', format)
    const h = Math.floor((seconds % oneDayInSecs) / oneHourInSecs)
    const parts = [formatUnit(d, 'day', format)]
    if (h > 0) parts.push(formatUnit(h, 'hour', format))
    return parts.join(sep)
  }
  if (seconds >= oneHourInSecs) {
    const h = Math.floor(seconds / oneHourInSecs)
    const m = Math.floor((seconds % oneHourInSecs) / oneMinInSecs)
    if (format === 'short') {
      if (h >= 10 || m === 0) return `${h}h`
      return `${h}h${String(m).padStart(2, '0')}`
    }
    if (!granular) return formatUnit(h, 'hour', format)
    const parts = [formatUnit(h, 'hour', format)]
    if (m > 0) parts.push(formatUnit(m, 'minute', format))
    return parts.join(sep)
  }
  const m = Math.max(1, Math.floor(seconds / oneMinInSecs))
  return formatUnit(m, 'minute', format)
}

export function dateLabelForUnixTimestamp(timestamp: number): string {
  return new Date(toJsTimestamp(timestamp)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Formats a Unix timestamp as a local date and time, e.g. "Apr 30, 2026, 14:30".
 * Use this when the exact moment matters; pair with `DateTimePopover` to show
 * the timezone breakdown on hover.
 */
export function dateTimeLabelForUnixTimestamp(timestamp: number): string {
  const date = new Date(toJsTimestamp(timestamp))
  const datePart = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
  const timePart = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })
  return `${datePart}, ${timePart}`
}
